import {
  buildOrderLiteral,
  buildPagingLiteral,
  buildWhereLiteral,
  OrderBy,
} from '@/lib/graphql-query-builder';

// Schemas: SurveyBatch / SurveySubmission (verified against
// SCHEMA_REFERENCE.json). The gateway names the query field by naive concatenation --
// "getSurveyBatchs", not the grammatically-plural "getSurveyBatches" -- confirmed against
// `blocks data schema get` (querySchema: "SurveyBatchs"). Aliased back to "SurveyBatches" so
// the rest of this file/consumers keep the grammatically-correct response key.
/** Newest send first by default, like the old `GetSurveys` `{ RequestDate: -1 }` sort (the batch's send time is `SentDateUtc`). */
export const DEFAULT_SURVEY_BATCHES_ORDER: OrderBy[] = [
  { field: 'SentDateUtc', direction: 'DESC' },
];

export const buildGetSurveyBatchesQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = DEFAULT_SURVEY_BATCHES_ORDER
) => `
  query {
    SurveyBatches: getSurveyBatchs(
      where: ${buildWhereLiteral(where)}
      order: ${buildOrderLiteral(order)}
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      hasNextPage
      hasPreviousPage
      totalCount
      totalPages
      pageSize
      pageNo
      items {
        ItemId
        Title
        Description
        SurveyFormId
        SentDateUtc
        Deadline
        Timezone
        TotalRecipients
        CompletedCount
      }
    }
  }
`;

/** Recipients by name by default, like the old `GetSurveyByBatchId` `{ UserName: 1 }` sort. */
export const DEFAULT_SURVEY_SUBMISSIONS_ORDER: OrderBy[] = [
  { field: 'UserName', direction: 'ASC' },
];

export const buildGetSurveySubmissionsQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = DEFAULT_SURVEY_SUBMISSIONS_ORDER
) => `
  query {
    SurveySubmissions: getSurveySubmissions(
      where: ${buildWhereLiteral(where)}
      order: ${buildOrderLiteral(order)}
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      hasNextPage
      hasPreviousPage
      totalCount
      totalPages
      pageSize
      pageNo
      items {
        ItemId
        SurveyBatchId
        SurveyFormId
        SurveyTitle
        SurveyDescription
        UserId
        UserName
        UserEmail
        Deadline
        Status
        RequestedDateUtc
        SubmittedDateUtc
        Timezone
        AnswersJson
      }
    }
  }
`;

// Schema: SurveyForm (same entity hr-survey-form-creation manages).
// HasSections/SectionsJson are required here too: without them, viewing a
// sectioned survey's responses would render an empty question list (only
// QuestionsJson would be read, which is empty for sectioned forms).
/** Latest change first, like the old send dialog's form list (`GetSurveyForms`, `{ LastUpdateDate: -1 }`). */
export const buildGetSurveyFormsLookupQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) => `
  query {
    SurveyForms: getSurveyForms(
      where: ${buildWhereLiteral(where)}
      order: ${buildOrderLiteral([{ field: 'LastUpdatedDate', direction: 'DESC' }])}
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      totalCount
      items {
        ItemId
        Title
        HasSections
        QuestionsJson
        SectionsJson
      }
    }
  }
`;

// There is no "User" data schema (same as hr-directory's manager lookup) -- people are looked up
// from the "UserProfileDetail" schema instead. The gateway's real query field is
// "getUserProfileDetails"; aliased to "Users" so the existing service/component code below (which
// reads `result.Users`) doesn't need to change.
/** Latest change first, like the old recipient picker (`GetEmployeeListInfo`, `{ LastUpdateDate: -1 }`). */
export const buildGetEmployeesLookupQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) => `
  query {
    Users: getUserProfileDetails(
      where: ${buildWhereLiteral(where)}
      order: ${buildOrderLiteral([{ field: 'LastUpdatedDate', direction: 'DESC' }])}
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      totalCount
      items {
        ItemId
        FirstName
        LastName
        DisplayName
        Email
      }
    }
  }
`;
